import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import axios from "axios";
import { trackViewContent } from "./utils/tracking";
import { safeJsonLd } from "./utils/safeJsonLd";
import PostHeader from "./components/blog/PostHeader";
import PostFooter from "./components/blog/PostFooter";
import PostHero from "./components/blog/PostHero";
import PostContent from "./components/blog/PostContent";
import Anim from "./components/blog/Anim";
import PostBottom, { PostCtaBar, PostRelated, PostDisclaimer } from "./components/blog/PostBottom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const [postRes, listRes] = await Promise.all([
          axios.get(`${API}/blog/${slug}`),
          axios.get(`${API}/blog`),
        ]);
        setPost(postRes.data.post);
        setAllPosts(listRes.data.posts || []);
        trackViewContent({ content_name: postRes.data.post?.title, content_type: 'blog_post' });
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const readTime = post ? Math.max(3, Math.ceil((post.content?.length || 500) / 1000)) : 3;

  if (loading) {
    return <div className="min-h-screen bg-bone flex items-center justify-center"><p className="text-text_muted">Loading article...</p></div>;
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center flex-col gap-4" data-testid="post-not-found">
        <h1 className="font-serif text-3xl text-emerald_deep">Article Not Found</h1>
        <p className="text-text_muted">The article you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/blog" className="inline-flex items-center gap-2 text-accent hover:underline text-sm"><ArrowLeft size={14} /> Back to Blog</Link>
      </div>
    );
  }

  const articleStructuredData = {
    "@context": "https://schema.org", "@type": "Article",
    headline: post.title, description: post.meta_description || post.excerpt, image: post.featured_image,
    author: { "@type": "Organization", name: post.author || "Fitly India" },
    publisher: { "@type": "Organization", name: "Fitly India", url: "https://fitlyindia.org" },
    datePublished: post.created_at, dateModified: post.updated_at || post.created_at,
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://fitlyindia.org/blog/${post.slug}` }
  };

  const breadcrumbData = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://fitlyindia.org/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://fitlyindia.org/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: `https://fitlyindia.org/blog/${post.slug}` }
    ]
  };

  return (
    <div className="bg-bone text-text_main" data-testid="blog-post-page">
      <Helmet>
        <title>{`${post.title} | Fitly India Blog`}</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <link rel="canonical" href={`https://fitlyindia.org/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://fitlyindia.org/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(articleStructuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(breadcrumbData) }} />

      <PostHeader scrolled={scrolled} />
      <main>
        <PostHero post={post} readTime={readTime} />
        <PostBottom featuredImage={post.featured_image} title={post.title} />
        <PostContent content={post.content} />
        <PostCtaBar />
        <PostRelated slug={slug} tags={post.tags || []} allPosts={allPosts} />
        <PostDisclaimer />
      </main>
      <PostFooter />
    </div>
  );
}
